import {AuthToken, EnsureFreshToken, IsReadOnly, Reauthenticate, Subject} from "./auth.js";
import {$$} from "./dom.js";

// Everything the drawer remembers for the signed-in user, in ONE entry, stamped with
// whose it is.
//
// Together rather than as sibling keys so the session is discarded as a unit: there is no
// order in which half of it survives, and a value added here later cannot be forgotten by
// a clearing routine that does not know about it. The stamp is the token's subject — an
// opaque id — so an entry that is not yours reads as absent and is overwritten on the next
// write, which is the same outcome as it never having been there.
//
// sessionStorage outlives a re-login in the same tab, which is how one operator inherited
// another's cluster and saw an empty drawer.
const KeySession = "drawer-session";

function readSession() {
	let stored = null;
	try {
		stored = JSON.parse(window.sessionStorage.getItem(KeySession) || "null");
	} catch {
		// Unparseable, or written by an older version. Starting over is always safe.
	}
	if (stored && stored.owner === Subject()) return stored;
	// Not ours: drop it rather than leave it to be read again or half-overwritten later.
	if (stored) window.sessionStorage.removeItem(KeySession);
	return {};
}

function writeSession(patch) {
	const next = {...readSession(), ...patch, owner: Subject()};
	window.sessionStorage.setItem(KeySession, JSON.stringify(next));
}

/** Drop the whole drawer session, e.g. on sign-out. */
export function ClearSession() {
	window.sessionStorage.removeItem(KeySession);
}

export function ClearSelectedApp() {
	writeSession({lastWorkload: null});
}

export function SelectedApp() {
	return readSession().lastWorkload || null;
}

function shortName(fullappname) {
	const pair = fullappname.split(":");
	return pair.length > 2 ? pair.slice(2).join(":") : fullappname;
}

function pathLabel(fullappname) {
	const pair = fullappname.split(":");
	return pair.length >= 2 ? `${pair[0]} · ${pair[1]}` : "";
}

function recordOpen(fullappname) {
	// Only asks for the connection. The selection is recorded by the ConnectStream
	// listener below, so that EVERY way of starting a stream records it — a drawer
	// click, the prev/next arrows, or the restore-on-load — instead of just this one.
	document.dispatchEvent(new CustomEvent("ConnectStream", {detail: {workload: fullappname}}));
}

function createAppButton(fullappname, showPath) {
	const btn = document.createElement("button");
	btn.type = "button";
	btn.dataset.app = fullappname;
	btn.id = "idapp-" + fullappname;
	btn.tabIndex = -1;
	btn.classList.add("btn", "btn-outline-secondary", "app-row");

	const nameSpan = document.createElement("span");
	nameSpan.className = "app-row__name";
	nameSpan.textContent = shortName(fullappname);
	btn.appendChild(nameSpan);

	if (showPath) {
		const pathSpan = document.createElement("span");
		pathSpan.className = "app-row__path";
		pathSpan.textContent = pathLabel(fullappname);
		btn.appendChild(pathSpan);
	}

	btn.addEventListener("click", () => recordOpen(fullappname));
	return btn;
}

function createSeparator(label) {
	const el = document.createElement("div");
	el.className = "app-row__separator";
	el.textContent = label;
	return el;
}

function createEmptyState() {
	const el = document.createElement("div");
	el.className = "app-empty";
	el.innerHTML = `
		<div class="app-empty__icon">⬡</div>
		<div class="app-empty__title">No apps available</div>
		<div class="app-empty__hint">Apps will appear here when they start streaming.</div>
	`;
	return el;
}

function createLoadingState() {
	const el = document.createElement("div");
	el.className = "app-loading";
	el.textContent = "Loading apps…";
	return el;
}

function attachKeyboardNav(listNode) {
	listNode.addEventListener("keydown", (e) => {
		if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(e.key)) return;
		const buttons = [...listNode.querySelectorAll("button[data-app]")].filter(
			(b) => b.style.display !== "none",
		);
		if (buttons.length === 0) return;
		const idx = buttons.indexOf(document.activeElement);
		let next = idx;
		if (e.key === "ArrowDown") next = idx < 0 ? 0 : Math.min(buttons.length - 1, idx + 1);
		else if (e.key === "ArrowUp") next = idx < 0 ? buttons.length - 1 : Math.max(0, idx - 1);
		else if (e.key === "Home") next = 0;
		else if (e.key === "End") next = buttons.length - 1;
		if (next !== idx) {
			e.preventDefault();
			buttons.forEach((b, i) => (b.tabIndex = i === next ? 0 : -1));
			buttons[next].focus();
		}
	});
}

class AppListView {
	constructor(node) {
		this.node = node;
		this.showPath = !IsReadOnly();
		this.children = new Map(); // key -> element
		this.clusterPills = null;
		this.nsPills = null;
		node.innerHTML = "";
		node.appendChild(createLoadingState());
		attachKeyboardNav(node);
	}

	ensureShell() {
		if (this.clusterPills && this.clusterPills.parentNode === this.node) return;
		this.node.innerHTML = "";
		this.children.clear();
		this.clusterPills = document.createElement("div");
		this.clusterPills.className = "apps-pills apps-pills--cluster";
		this.clusterPills.dataset.label = "Cluster";
		this.nsPills = document.createElement("div");
		this.nsPills.className = "apps-pills apps-pills--ns";
		this.nsPills.dataset.label = "Namespace";
		this.node.appendChild(this.clusterPills);
		this.node.appendChild(this.nsPills);
	}

	teardownShell() {
		this.node.innerHTML = "";
		this.children.clear();
		this.clusterPills = null;
		this.nsPills = null;
	}

	updatePills(clusters) {
		const {cluster: activeCluster, ns: activeNs} = activeSelection(clusters);
		const clusterNames = Object.keys(clusters).sort();

		this.clusterPills.innerHTML = "";
		clusterNames.forEach((c) => {
			const pill = document.createElement("button");
			pill.type = "button";
			pill.className = "apps-pill";
			if (c === activeCluster) pill.classList.add("--active");
			pill.textContent = c;
			pill.addEventListener("click", () => {
				const nsNames = Object.keys(clusters[c]).sort();
				const ns = nsNames.includes(activeNs) ? activeNs : nsNames[0];
				writeSession({preferedNS: `${c}:${ns}`});
				document.dispatchEvent(new CustomEvent("apps-ns-changed"));
			});
			this.clusterPills.appendChild(pill);
		});
		this.clusterPills.style.display = clusterNames.length > 1 ? "" : "none";

		const nsNames = (clusters[activeCluster] ? Object.keys(clusters[activeCluster]) : []).sort();
		this.nsPills.innerHTML = "";
		nsNames.forEach((n) => {
			const pill = document.createElement("button");
			pill.type = "button";
			pill.className = "apps-pill";
			if (n === activeNs) pill.classList.add("--active");
			pill.textContent = `${n} (${clusters[activeCluster][n].length})`;
			pill.addEventListener("click", () => {
				writeSession({preferedNS: `${activeCluster}:${n}`});
				document.dispatchEvent(new CustomEvent("apps-ns-changed"));
			});
			this.nsPills.appendChild(pill);
		});
		this.nsPills.style.display = nsNames.length > 0 ? "" : "none";
	}

	renderItems(items) {
		const selected = SelectedApp();
		const seen = new Set();
		const desired = [];

		items.forEach((item) => {
			let key, el;
			if (item.type === "app") {
				key = "app:" + item.appname;
				el = this.children.get(key) || createAppButton(item.appname, this.showPath);
				el.classList.toggle("active", item.appname === selected);
			} else if (item.type === "separator") {
				key = "sep:" + item.label;
				el = this.children.get(key) || createSeparator(item.label);
			} else {
				key = "empty";
				el = this.children.get(key) || createEmptyState();
			}
			this.children.set(key, el);
			seen.add(key);
			desired.push(el);
		});

		this.children.forEach((el, key) => {
			if (!seen.has(key)) {
				if (el.parentNode) el.parentNode.removeChild(el);
				this.children.delete(key);
			}
		});

		const headCount = this.clusterPills && this.clusterPills.parentNode === this.node ? 2 : 0;
		desired.forEach((el, i) => {
			const cur = this.node.children[headCount + i];
			if (cur !== el) {
				if (cur) this.node.insertBefore(el, cur);
				else this.node.appendChild(el);
			}
		});
		while (this.node.children[headCount + desired.length]) {
			this.node.removeChild(this.node.children[headCount + desired.length]);
		}

		const visibleButtons = [...this.node.querySelectorAll("button[data-app]")].filter(
			(b) => b.style.display !== "none",
		);
		visibleButtons.forEach((b, i) => (b.tabIndex = i === 0 ? 0 : -1));
	}
}

let _views = [];
let _lastAppslist = null;
let _lastClusters = null;

// Which cluster and namespace the drawer is showing.
//
// Validated against the apps actually present, never taken on trust: the stored value
// survives a re-login in the same tab, so a user who can see one cluster inherits a
// selection naming a cluster they cannot see. Everything then filters to nothing, and
// because the cluster row hides itself when there is only one cluster there is no pill
// left to click — the apps are reachable only by searching, which skips the filter.
function activeSelection(clusters) {
	const clusterNames = Object.keys(clusters).sort();
	if (clusterNames.length === 0) return {cluster: null, ns: null};

	const [storedCluster, storedNs] = (readSession().preferedNS || "").split(":");
	const cluster = clusters[storedCluster] ? storedCluster : clusterNames[0];
	const nsNames = Object.keys(clusters[cluster]).sort();
	return {cluster, ns: nsNames.includes(storedNs) ? storedNs : nsNames[0]};
}

function computeItems(appslist, clusters) {
	if (appslist.length === 0) return [{type: "empty"}];

	const {cluster, ns} = activeSelection(clusters);
	const visibleApps = (cluster && clusters[cluster][ns]) || [];

	return visibleApps.map((a) => ({type: "app", appname: a}));
}

export function SiblingApp(direction) {
	if (!_lastClusters) return null;
	const selected = SelectedApp();
	if (!selected) return null;
	const parts = selected.split(":");
	if (parts.length < 2) return null;
	const siblings = (_lastClusters[parts[0]] && _lastClusters[parts[0]][parts[1]]) || [];
	if (siblings.length < 2) return null;
	const idx = siblings.indexOf(selected);
	if (idx < 0) return null;
	const delta = direction === "prev" ? -1 : 1;
	const next = (idx + delta + siblings.length) % siblings.length;
	return siblings[next];
}

function applyToViews() {
	if (_lastAppslist === null) return;
	_views.forEach((view) => {
		if (_lastAppslist.length === 0) {
			view.teardownShell();
			view.renderItems(computeItems(_lastAppslist, _lastClusters));
			return;
		}
		view.ensureShell();
		view.updatePills(_lastClusters);
		view.renderItems(computeItems(_lastAppslist, _lastClusters));
	});
	document.dispatchEvent(new CustomEvent("apps-updated"));
}

async function fetchApps() {
	let appslist;
	// This poll is the whole session's heartbeat, so it is also where the access token is
	// kept alive. No separate timer, and it covers the stream view as well as the drawer.
	await EnsureFreshToken();
	try {
		const resp = await fetch("/api/apps", {
			method: "GET",
			headers: {Authorization: "Bearer " + AuthToken()},
		});
		// The token expired (or was never valid). This polls every 5s, so without
		// this the list just stops updating and the only clue is the console.
		if (resp.status === 401) {
			Reauthenticate();
			return;
		}
		if (!resp.ok) {
			console.error("fetchApps failed", resp.status);
			return;
		}
		// Case-insensitive: a plain .sort() compares code points, so an app named
		// "apple" lands after "Zulu" and the drawer reads as unordered. This order is
		// also what prev/next walks, so it has to be the order a human sees.
		appslist = (await resp.json()).sort((a, b) =>
			a.localeCompare(b, undefined, {sensitivity: "base"}),
		);
	} catch (e) {
		console.error("fetchApps failed", e);
		return;
	}

	const clusters = {};
	appslist.forEach((appname) => {
		const pair = appname.split(":");
		const c = pair[0];
		const n = pair[1];
		clusters[c] = clusters[c] || {};
		clusters[c][n] = clusters[c][n] || [];
		clusters[c][n].push(appname);
	});

	_lastAppslist = appslist;
	_lastClusters = clusters;
	applyToViews();
}

export function StartListenApps() {
	_views = $$(".appslist").map((node) => new AppListView(node));

	document.addEventListener("apps-ns-changed", () => applyToViews());
	// Records which app is now selected, then re-renders. This is the only writer of
	// the drawer session, and it has to be: SelectedApp() is the anchor SiblingApp()
	// steps from, so a path that connects without recording leaves prev/next walking
	// from wherever the last drawer click left them. That is what made the arrows
	// appear to skip around — every press computed the neighbour of the same app.
	document.addEventListener("ConnectStream", (event) => {
		const workload = /** @type {CustomEvent} */ (event).detail?.workload;
		if (workload) writeSession({lastWorkload: workload});
		applyToViews();
	});

	fetchApps();
	setInterval(fetchApps, 5000);
}
